/* eslint-disable no-undef */
import React, {useContext, useEffect, useState} from 'react';
import './AlarmsTable.scss'
import {
  DataTable,
  DataTableSkeleton,
  OverflowMenu,
  OverflowMenuItem,
  Pagination,
  Table,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarMenu,
  TableToolbarSearch,
  Tooltip,
} from "@carbon/react";
import {Archive, Email, EmailNew, Error, Maximize, Renew, SendAlt, StopSignFilled} from '@carbon/react/icons';
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {
  editAlarmNoteModalNotesState,
  editAlarmNoteModalOpenState,
  editAlarmPriorityModalOpenState,
  editAlarmPriorityModalPriorityState,
  errorState,
  filtersSelector,
  firstRowIndexState,
  forceCloseAlarmsModalIdsState,
  forceCloseAlarmsModalOpenState,
  forwardAlarmsState,
  forwardToSupportAlarmModalOpenState,
  loadingState,
  pageSizeState,
  postponeAlarmModalOpenState,
  reportToUserModalOpenState,
  rowsState,
  selectedAlarmState,
  viewCustomerDetailModalCustomerCodeState,
  viewCustomerDetailModalOpenState,
} from "../../state/alerts_table";
import {grpcAlarmServiceSelector, grpcDeviceServiceSelector} from "../../state/grpc";
import EditAlertNoteModal from "./EditAlertNoteModal";
import EditAlertPriorityModal from "./EditAlertPriorityModal";
import BatchCloseModal from "./BatchCloseModal";
import AlarmExpandableContent from "./AlarmExpandableContent";
import authContext from "../Auth/AuthContext";
import {ADD, useToastContext} from "../Toast";
import CustomerDetailModal from "./CustomerDetailModal";
import ForwardToSupportModal from "./ForwardToSupportModal";
import {useNavigate} from "react-router-dom";
import PubSub from "pubsub-js";
import ReportToUserModal from "./ReportToUserModal";
import TableToolbarFilters from "./TableToolbarFilters";
import PostponeNotificationModal from "./PostponeNotificationModal";
import EditAlertReadmeModal from "./EditAlertReadmeModal";
import PropTypes from "prop-types";

const AlarmsTable = (props) => {
  const headers = [{
    key: 'isManual', header: ''
  },{
    key: 'ownerUsername', header: ''
  }, {
    key: 'customerName', header: 'Sistema'
  }, {
    key: 'name', header: 'Allarme'
  }, {
    key: 'instrumentName', header: 'Strumento'
  }, {
    key: 'openedAt', header: 'Apertura'
  }, {
    key: 'customer24Service', header: 'H24'
  }, {
    key: 'longDesc', header: 'Expanded'
  }];

  const cloudConnected = props.cloudConnected
  const updateRunningRecording = props.updateRunningRecording
  const runningRecording = props.runningRecording
  const authCtx = useContext(authContext)
  const {toastDispatch} = useToastContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useRecoilState(loadingState)
  const [error, setError] = useRecoilState(errorState)
  const [initialRows, setRows] = useRecoilState(rowsState)
  const [firstRowIndex, setFirstRowIndex] = useRecoilState(firstRowIndexState)
  const [pageSize, setPageSize] = useRecoilState(pageSizeState)
  const [, setSelectedAlarm] = useRecoilState(selectedAlarmState)
  const setForwardAlarms = useSetRecoilState(forwardAlarmsState)
  const [, setEditAlarmNoteModalOpen] = useRecoilState(editAlarmNoteModalOpenState)
  const [, setEditAlarmNoteModalNotes] = useRecoilState(editAlarmNoteModalNotesState)
  const [, setEditAlarmPriorityModalOpen] = useRecoilState(editAlarmPriorityModalOpenState)
  const [, setEditAlarmPriorityModalPriority] = useRecoilState(editAlarmPriorityModalPriorityState)
  const [, setForceCloseAlarmsModalOpen] = useRecoilState(forceCloseAlarmsModalOpenState)
  const [, setForceCloseAlarmsModalIdsState] = useRecoilState(forceCloseAlarmsModalIdsState)
  const [, setViewCustomerDetailModalOpen] = useRecoilState(viewCustomerDetailModalOpenState)
  const [, setViewCustomerDetailModalCustomerCode] = useRecoilState(viewCustomerDetailModalCustomerCodeState)
  const [, setForwardToSupportAlarmModalOpen] = useRecoilState(forwardToSupportAlarmModalOpenState)
  const setPostponeAlarmOpen = useSetRecoilState(postponeAlarmModalOpenState)
  const setReportToUserModalOpen = useSetRecoilState(reportToUserModalOpenState)
  const [alertReadmeModalOpen, setAlertReadmeModalOpen] = useState(false)
  const [modalAlarm, setModalAlarm] = useState(null)
  const alarmsService = useRecoilValue(grpcAlarmServiceSelector)
  const deviceService = useRecoilValue(grpcDeviceServiceSelector)
  const [filters, setFilters] = useRecoilState(filtersSelector)
  const username = authCtx.data().username

  const getSeverityListFromState = () => {
    return Object.keys(filters)
      .filter(key => key.startsWith("filter_severity_"))
      .filter(key => filters[key].enabled)
      .map((key) => filters[key].value)
  }

  const getH24OnlyFromState = () => {
    return filters["filter_h24_only"].enabled
  }

  const alarmMatchesFilters = (alarm) => {
    if (!getSeverityListFromState().includes(alarm.severity)) {
      return false
    }
    if (getH24OnlyFromState() && !alarm.customer24Service) {
      return false
    }
    return true
  }

  function getAlarmsTableData() {
    const req = new proto.api.ListAlarmsRequest();
    req.setSeverityList(getSeverityListFromState())
    req.setH24Only(getH24OnlyFromState())
    const pageReq = new proto.api.PageRequest()
    pageReq.setSort("notify_schedule asc")
    req.setStatus("Aperto")
    req.setPagination(pageReq)
    setLoading(true)
    setFirstRowIndex(0)
    alarmsService.listAlarms(req, null, (err, resp) => {
      if (err != null) {
        setError(true)
      } else {
        setRows(resp.toObject().alarmsList)
        setLoading(false)
        setError(false)
      }
    })
  }

  const handleAlarmEvent = (msg, data) => {
    const alarm = data.alarm
    const eventType = data.eventType
    // this alarm is filtered out from the list, we don't have to handle it
    if (!alarmMatchesFilters(alarm)) {
      return
    }
    switch (eventType) {
      case proto.api.SubscriptionUpdateType.GRD_ALARM_OPENED: {
        setRows((currentState) => [
          ...currentState,
          alarm,
        ])
        break
      }
      case proto.api.SubscriptionUpdateType.GRD_ALARM_CLOSED:
      case proto.api.SubscriptionUpdateType.GRD_ALARM_UPDATED: {
        setRows(
          initialRows.map((row) => ({
            ...row,
            severity: alarm.id === row.id ? alarm.severity : row.severity,
            notes: alarm.id === row.id ? alarm.notes : row.notes,
            ownerUsername: row.id === alarm.id ? alarm.ownerUsername : row.ownerUsername,
            ownerImageUrl: row.id === alarm.id ? alarm.ownerImageUrl : row.ownerImageUrl,
            forwardedToSupport: row.id === alarm.id ? alarm.forwardedToSupport : row.forwardedToSupport,
            status: alarm.id === row.id ? alarm.status : row.status,
            firstNotifySchedule: alarm.id === row.id ? alarm.firstNotifySchedule : row.firstNotifySchedule,
            notifySchedule: alarm.id === row.id ? alarm.notifySchedule : row.notifySchedule,
          }))
        )
        break
      }
    }
  }

  useEffect(() => {
    getAlarmsTableData()
  }, [filters, cloudConnected]);

  useEffect(() => {
    if (!initialRows || initialRows.length === 0) {
      return
    }
    const token = PubSub.subscribe('ALARM_EVENT', handleAlarmEvent)
    return () => {
      PubSub.unsubscribe(token)
    }
  }, [initialRows])

  if (loading) {
    return (<DataTableSkeleton
      columnCount={headers.length + 1}
      rowCount={5}
      headers={headers}
    />)
  }

  if (error) {
    return (<div>
      <span>ERROR!</span>
    </div>)
  }

  const lookupDeviceAlarm = (systemCode, alarmCode, deviceCode) => {
    const req = new proto.api.FindDeviceAlarmsRequest()
    req.setSystemCode(systemCode)
    req.setDeviceCode(deviceCode)
    req.setAlarmCode(alarmCode)
    deviceService.findDeviceAlarm(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
      } else {
        const deviceAlarm = resp.toObject().devicealarm
        navigate(`/customers/${systemCode}/devices/${deviceAlarm.deviceId}`)
      }
    })
  }

  const processChildren = (alarm, cell) => {
    switch (cell.info.header) {
      case 'customer24Service': {
        return (
          <div title={cell.value ? "Attivo" : "Disattivo"} className={cell.value ? "light green" : "light red"}/>
        )
      }
      case 'isManual': {
        if (alarm.isManual) {
          return (
            <Tooltip align="bottom" label="Chiusura manuale">
              <button className="alarm-manual-icon" title={alarm.ownerUsername}>
                <StopSignFilled />
              </button>
            </Tooltip>
          )
        }
        return (
          <span/>
        )
      }
      case 'ownerUsername': {
        if (alarm.ownerUsername) {
          return (
            <Tooltip align="bottom" label={alarm.ownerUsername}>
              <button className="avatar-image" title={alarm.ownerUsername}>
                {alarm.ownerImageUrl && <img src={alarm.ownerImageUrl + ".png"} alt={alarm.ownerUsername}/>}
              </button>
            </Tooltip>
          )
        }
        return (
          <Tooltip align="bottom" label="Nessun titolare">
            <button className="avatar-image" title="Nessun titolare">
              <Error/>
            </button>
          </Tooltip>
        )
      }
      case 'customerName': {
        return (
          <span className="customer-link" title="Dettagli sistema"
                onClick={() => {
                  setViewCustomerDetailModalOpen(true)
                  setViewCustomerDetailModalCustomerCode(alarm.customerCode)
                }}>
            {alarm.customerName}
          </span>
        )
      }
      case 'instrumentName': {
        return (
          <span className="customer-link" title="Configura strumento"
                onClick={() => lookupDeviceAlarm(alarm.customerCode, alarm.code, alarm.instrumentCode)}>
            {alarm.instrumentCode ? alarm.instrumentCode + ": " : ""}
            {alarm.instrumentName}
          </span>
        )
      }
      case 'name': {
        return (
          <>
            {alarm.code + ": " + alarm.name}
          </>
        )
      }
      default: {
        return (
          <>
            {cell.value}
          </>
        )
      }
    }
  }

  const handlePaginationChange = (page, newPageSize) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    setFirstRowIndex(newPageSize * (page - 1));
  }

  const batchCloseAlarms = (ids) => {
    setForceCloseAlarmsModalIdsState(ids.map(it => it.id))
    setForceCloseAlarmsModalOpen(true)
  }

  const closeAlarm = (id) => {
    batchCloseAlarms([{id: id}])
  }

  const handleTakeOwnership = (id) => {
    const req = new proto.api.TakeOwnershipRequest()
    req.setAlarmid(id)
    alarmsService.takeOwnership(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
        toastDispatch({
          type: ADD,
          payload: {
            title: "Operazione fallita",
            content: "Non è stato possibile prendere in carico l'allarme",
            kind: "error",
            caption: err.message
          }
        })
      } else {
        // updateAlarmOwnership(respStruct.alarmid, respStruct.username, respStruct.userimageurl)
        toastDispatch({
          type: ADD,
          payload: {
            title: "Operazione avvenuta con successo",
            content: "Allarme preso in carico",
            kind: "success"
          }
        })
      }
    })
  }

  const handleBatchRead = (selectedRows, isRead) => {
    const ids = selectedRows.map(it => it.id)
    const req = new proto.api.MarkAsReadRequest()
    req.setAlarmidsList(ids)
    req.setRead(isRead)
    alarmsService.markAsRead(req, null, (err) => {
      if (err != null) {
        console.error(err)
      } else {
        updateAlarmRead(ids, isRead)
      }
    })
  }

  const updateAlarmRead = (alarmIds, isRead) => {
    setRows(
      initialRows.map((row) => ({
        ...row,
        read: alarmIds.includes(row.id) ? isRead : row.read
      }))
    );
  }

  const onFiltersChange = (filters) => {
    setFilters(filters)
  }

  return (
    <>
      <CustomerDetailModal updateRunningRecording={updateRunningRecording} runningRecording={runningRecording}/>
      <EditAlertNoteModal/>
      <EditAlertPriorityModal/>
      <ForwardToSupportModal/>
      <BatchCloseModal onAlarmsClosed={() => {
        setForceCloseAlarmsModalOpen(false)
        getAlarmsTableData()
      }}/>
      <ReportToUserModal/>
      <PostponeNotificationModal onAlarmPostponed={() => {
        setPostponeAlarmOpen(false)
        getAlarmsTableData()
      }}/>
      <EditAlertReadmeModal
        alarm={modalAlarm}
        isOpen={alertReadmeModalOpen}
        onClose={(modified, readme) => {
          setAlertReadmeModalOpen(false)
          if (modified) {
            setRows(
              initialRows.map((row) => ({
                ...row,
                readme: row.id === modalAlarm.id ? readme : row.readme
              }))
            );
          }
        }}
      />
      <DataTable
        rows={initialRows}
        headers={headers}
        isSortable
        overflowMenuOnHover={false}
        locale="it"
        render={({
                   rows,
                   headers,
                   getHeaderProps,
                   getRowProps,
                   getToolbarProps,
                   onInputChange,
                   getTableProps,
                   getTableContainerProps,
                   getExpandHeaderProps,
                   getBatchActionProps,
                   getSelectionProps,
                   selectedRows,
                 }) => {
          const batchActionProps = getBatchActionProps();
          return (
            <TableContainer title="Allarmi attivi"
                            description="Lista degli allarmi attivi"
                            className="active-alarms-table"
                            {...getTableContainerProps()}>
              <TableToolbar {...getToolbarProps()} >
                <TableBatchActions
                  {...batchActionProps}
                  translateWithId={(id, state) => {
                    if (id === 'carbon.table.batch.cancel') {
                      return "Annulla"
                    }
                    return `${state.totalSelected} ` + (state.totalSelected > 1 ? "elementi selezionati" : "elemento selezionato")
                  }}>
                  <TableBatchAction
                    tabIndex={batchActionProps.shouldShowBatchActions ? 0 : -1}
                    renderIcon={Email}
                    onClick={() => handleBatchRead(selectedRows, true)}>
                    Segna come letti
                  </TableBatchAction>
                  <TableBatchAction
                    tabIndex={batchActionProps.shouldShowBatchActions ? 0 : -1}
                    renderIcon={EmailNew}
                    onClick={() => handleBatchRead(selectedRows, false)}>
                    Segna come da leggere
                  </TableBatchAction>
                  <TableBatchAction
                    tabIndex={batchActionProps.shouldShowBatchActions ? 0 : -1}
                    renderIcon={SendAlt}
                    onClick={() => {
                      setForwardAlarms(selectedRows.map(it => it.id))
                      setForwardToSupportAlarmModalOpen(true)
                    }}>
                    Inoltra
                  </TableBatchAction>
                  <TableBatchAction
                    tabIndex={batchActionProps.shouldShowBatchActions ? 0 : -1}
                    renderIcon={Archive}
                    onClick={() => batchCloseAlarms(selectedRows)}>
                    Forza chiusura
                  </TableBatchAction>
                </TableBatchActions>
                <TableToolbarContent aria-hidden={batchActionProps.shouldShowBatchActions}>
                  <TableToolbarSearch
                    persistent
                    placeholder="Filtra gli allarmi attivi.."
                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                    onChange={onInputChange}/>
                  <TableToolbarFilters filtersState={filters} onFiltersChange={onFiltersChange}/>
                  <TableToolbarMenu
                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                    renderIcon={Renew}
                    onClick={() => getAlarmsTableData()}
                    light
                    iconDescription="Aggiorna" />
                </TableToolbarContent>
              </TableToolbar>
              <Pagination
                totalItems={rows.length}
                backwardText="Precedente"
                forwardText="Successiva"
                pageSize={pageSize}
                pageSizes={[10, 25, 50, 100]}
                itemsPerPageText="Elementi per pagina"
                pageRangeText={(_current, total) => `di ${total} ${total === 1 ? 'pagina' : 'pagine'}`}
                itemRangeText={(min, max, total) => `${min}–${max} of ${total} elementi`}
                onChange={({page, pageSize}) => handlePaginationChange(page, pageSize)}
              />
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    <TableExpandHeader {...getExpandHeaderProps()} />
                    <TableSelectAll {...getSelectionProps()} />
                    {headers.filter((header) => {
                      return header.key !== "longDesc"
                    }).map((header) => (
                      <TableHeader {...getHeaderProps({header})}>
                        {header.header}
                      </TableHeader>
                    ))}
                    <TableHeader scope="col"/>
                    <TableHeader scope="col"/>
                    <TableHeader scope="col"/>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows
                    .slice(firstRowIndex, firstRowIndex + pageSize)
                    .map((row) => {
                      return {
                        alarm: initialRows.find(it => it.id === row.id),
                        ...row
                      }
                    })
                    .map((row) => (
                      <React.Fragment key={row.id}>
                        <TableExpandRow
                          className={`alarm-row alarm-row-${row.id} alarm-status-${row.alarm.status} alarm-${row.alarm.severity} ` + (row.alarm.read ? "alarm-row-read" : "alarm-row-unread")}
                          {...getRowProps({row})}>
                          <TableSelectRow {...getSelectionProps({row})} />
                          {
                            row.cells.filter((cell) => cell.info.header !== "longDesc" && cell.info.header !== "id")
                              .map((cell) => (
                                <TableCell key={cell.id}>{processChildren(row.alarm, cell)}</TableCell>)
                              )
                          }
                          <TableCell className="cds--table-column-menu">
                            <OverflowMenu
                              flipped size='md' aria-label="Schermo intero"
                              iconDescription="Schermo intero"
                              renderIcon={Maximize}
                              onClick={() => {
                                navigate("/alarms/" + row.id)
                              }}/>
                          </TableCell>
                          <TableCell className="cds--table-column-menu">
                            <OverflowMenu
                              disabled={row.alarm.status !== "Aperto"}
                              flipped size='md'
                              aria-label={row.alarm.read ? "Segna come da leggere" : "Segna come già letto"}
                              iconDescription={row.alarm.read ? "Segna come da leggere" : "Segna come già letto"}
                              renderIcon={row.alarm.read ? EmailNew : Email}
                              onClick={() => handleBatchRead([row], !row.alarm.read)}/>
                          </TableCell>
                          <TableCell className="cds--table-column-menu">
                            <OverflowMenu
                              disabled={row.alarm.status !== "Aperto"}
                              flipped size='md' aria-label="Opzioni"
                              iconDescription="Opzioni">
                              <OverflowMenuItem
                                itemText="Cambia priorità"
                                onClick={() => {
                                  setEditAlarmPriorityModalOpen(true)
                                  setEditAlarmPriorityModalPriority(row.alarm.severity)
                                  setSelectedAlarm(row.alarm)
                                }}
                              />
                              <OverflowMenuItem
                                isDelete
                                itemText="Forza chiusura"
                                onClick={() => closeAlarm(row.id)}
                              />
                              <OverflowMenuItem
                                itemText="Modifica note"
                                hasDivider
                                onClick={() => {
                                  setEditAlarmNoteModalOpen(true)
                                  setEditAlarmNoteModalNotes(row.alarm.notes)
                                  setSelectedAlarm(row.alarm)
                                }}
                              />
                              <OverflowMenuItem
                                itemText="Modifica indicazioni"
                                hasDivider
                                onClick={() => {
                                  setAlertReadmeModalOpen(true)
                                  setModalAlarm(row.alarm)
                                }}
                              />
                              <OverflowMenuItem
                                itemText="Posponi notifica.."
                                onClick={() => {
                                  setPostponeAlarmOpen(true)
                                  setSelectedAlarm(row.alarm)
                                }}
                              />
                              {
                                row.alarm.ownerUsername !== username &&
                                <OverflowMenuItem
                                  itemText="Prendi in carico"
                                  onClick={() => handleTakeOwnership(row.id)}
                                />
                              }
                              <OverflowMenuItem
                                itemText="Notifica utente"
                                onClick={() => {
                                  setSelectedAlarm(row.alarm)
                                  setReportToUserModalOpen(true)
                                }}
                              />
                              <OverflowMenuItem
                                requireTitle
                                itemText="Inoltra"
                                onClick={() => {
                                  setForwardAlarms([row.alarm.id])
                                  setForwardToSupportAlarmModalOpen(true)
                                }}
                              />
                            </OverflowMenu>
                          </TableCell>
                        </TableExpandRow>
                        {row.isExpanded && (
                          <TableExpandedRow colSpan={headers.length + 4}>
                            <AlarmExpandableContent
                              alarm={row.alarm}
                              onContentExpanded={() => updateAlarmRead(new Array(row.id), true)}/>
                          </TableExpandedRow>
                        )}
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
              <Pagination
                className="pagination-bottom"
                totalItems={rows.length}
                backwardText="Precedente"
                forwardText="Successiva"
                pageSize={pageSize}
                pageSizes={[10, 25, 50, 100]}
                itemsPerPageText="Elementi per pagina"
                pageRangeText={(_current, total) => `di ${total} ${total === 1 ? 'pagina' : 'pagine'}`}
                itemRangeText={(min, max, total) => `${min}–${max} of ${total} elementi`}
                onChange={({page, pageSize}) => handlePaginationChange(page, pageSize)}
              />
            </TableContainer>
          );
        }}
      />
    </>
  )
}

AlarmsTable.propTypes = {
  cloudConnected: PropTypes.bool.isRequired,
  runningRecording: PropTypes.object,
  updateRunningRecording: PropTypes.func.isRequired,
}

export default AlarmsTable
